/**
 * The test enemies' builds. Their traits decide which of the attack bonuses pays off against
 * them, more than the weapon does:
 * - Slime: soft (it soaks up momentum), no armor or shield, lots of hp, can't be stunned:
 *   rotation. Finesse has nothing to beat.
 * - Critter: tiny: any hit kills it, so there's no point in a big run-up.
 * - Knight: armor and a shield that reads your swing: finesse (feint round it; it pierces armor).
 * - Raider, Charger: medium: movement (one well-placed blow stuns, knocks flying or kills).
 *   A charging Charger takes the speed of its own charge too.
 * - Brute: huge hp, resists stun and knockback: rotation.
 */
import type { Vector3 } from 'three/webgpu';
import type { Target } from './resolve';

export type Behaviour = 'stand' | 'hop' | 'skitter' | 'strafe' | 'charge';

/** A test enemy's build: its traits decide which bonus pays off against it. */
export interface Kind {
  name: string;
  /** What works on it, for its nameplate. */
  tip: string;
  hp: number;
  armor: number;
  mass: number;
  knockResist: number;
  /** Share of a blow's momentum it soaks up (soft or huge bodies). */
  cushion: number;
  poise: number;
  /** A shield that reads your swing: half-width (radians), share it stops, turn rate (rad/s). */
  shield?: { half: number; block: number; turn: number };
  behaviour: Behaviour;
  /** Its walking speed (m/s) and how fast it turns to face you (rad/s). */
  speed: number;
  turn: number;
  /** Body size (m): a capsule, or a ball when height ≤ 2·radius. And its colour. */
  radius: number;
  height: number;
  color: number;
}

const deg = Math.PI / 180;

export const SLIME: Kind = {
  name: 'Slime',
  tip: 'soft, no armor, lots of hp: spin',
  hp: 170,
  armor: 0,
  mass: 90,
  knockResist: 0.5,
  cushion: 0.5,
  poise: 99,
  behaviour: 'hop',
  speed: 1.5,
  turn: 2,
  radius: 0.62,
  height: 1.24,
  color: 0x62c45a,
};
export const CRITTER: Kind = {
  name: 'Critter',
  tip: 'any hit',
  hp: 6,
  armor: 0,
  mass: 8,
  knockResist: 0,
  cushion: 0,
  poise: 0,
  behaviour: 'skitter',
  speed: 4.5,
  turn: 12,
  radius: 0.2,
  height: 0.4,
  color: 0x6a4a8c,
};
export const KNIGHT: Kind = {
  name: 'Knight',
  tip: 'armor and a shield: feint round it',
  hp: 80,
  armor: 0.7,
  mass: 90,
  knockResist: 0.2,
  cushion: 0,
  poise: 2,
  shield: { half: 50 * deg, block: 0.85, turn: 6 },
  behaviour: 'stand',
  speed: 2,
  turn: 1.6,
  radius: 0.4,
  height: 1.9,
  color: 0x8b95a3,
};
export const RAIDER: Kind = {
  name: 'Raider',
  tip: 'medium: charge or jump in',
  hp: 70,
  armor: 0.1,
  mass: 75,
  knockResist: 0,
  cushion: 0,
  poise: 0.6,
  behaviour: 'strafe',
  speed: 2.2,
  turn: 4,
  radius: 0.38,
  height: 1.8,
  color: 0x6e4a30,
};
export const CHARGER: Kind = {
  name: 'Charger',
  tip: 'meet its charge (maul, spear)',
  hp: 80,
  armor: 0.2,
  mass: 110,
  knockResist: 0.1,
  cushion: 0,
  poise: 0.9,
  behaviour: 'charge',
  speed: 8.5,
  turn: 3,
  radius: 0.48,
  height: 1.9,
  color: 0x7a2e22,
};
export const BRUTE: Kind = {
  name: 'Brute',
  tip: 'huge and steady: spin',
  hp: 450,
  armor: 0.25,
  mass: 320,
  knockResist: 0.85,
  cushion: 0.25,
  poise: 4.5,
  behaviour: 'stand',
  speed: 1.5,
  turn: 1.2,
  radius: 0.8,
  height: 3,
  color: 0x5d6470,
};

/**
 * What a hit needs to know about an enemy of this kind: where it is, how far it moved during
 * the strike, and its shield's side (null: no shield, or it's down).
 */
export function targetOf(kind: Kind, center: Vector3, travel: Vector3, shieldDir: Vector3 | null): Target {
  return {
    center,
    travel,
    radius: kind.radius,
    armor: kind.armor,
    shieldDir: kind.shield ? shieldDir : null,
    shieldHalf: kind.shield?.half ?? 0,
    shieldBlock: kind.shield?.block ?? 0,
    mass: kind.mass,
    knockResist: kind.knockResist,
    cushion: kind.cushion,
    poise: kind.poise,
  };
}
